import { isPlatformBrowser, Location } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild, inject, DOCUMENT, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';


import { ILanguage } from '@www/core/language.model';
import { MobileMenuDirective } from '../../directives/mobile-menu/mobile-menu.directive';
import { RouteLanguageService } from '../../services/route-language.service';

/**
 * Navigation component.
 */
@Component({
    selector: 'app-navigation',
    templateUrl: './navigation.component.html',
    styleUrls: ['./navigation.component.scss'],
    standalone: false
})
export class NavigationComponent implements OnDestroy, OnInit {
  @ViewChild(MobileMenuDirective) mobileMenu: MobileMenuDirective;
  @Input() locale: string;
  @Input() filteredLanguages: ILanguage[];
  @Input() popularLanguages: ILanguage[];
  @Input() logoTopSrc: string;
  @Input() loginUrl: string;
  @Input() signupUrl: string;
  @Output() signedUpPopUp = new EventEmitter<void>();
  @Output() scrollPage = new EventEmitter<void>();
  @Output() languageChange = new EventEmitter<void>();

  public isHomePage: boolean;
  public isMobileMenuShown = false;
  public resourcesRoute = false;
  public language!: string;
  public isDropdownHidden = false;

  private location = inject(Location);
  private locationUnsubscribe!: VoidFunction;

  constructor(
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
    private routeLanguageService: RouteLanguageService,
    private cd: ChangeDetectorRef,
  ) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.isHomePage = event.url === '/home' || event.url === '/';
        this.hideNavigationDropdown();
      }

      if (event instanceof NavigationEnd) {
        this.isMobileMenuShown && this.showHideMobileMenu();

        if (isPlatformBrowser(this.platformId)) {
          requestAnimationFrame(() => this.showNavigationDropdown());
        }
      }
    });

    this.setLocation();
  }

  public ngOnDestroy(): void {
    this.locationUnsubscribe();
  }

  private setLocation(): void {
    this.locationUnsubscribe = this.location.onUrlChange((url) => {
      if (url.includes('/faq') || url.includes('/documentation') || url.includes('/blog')) {
        !this.resourcesRoute && (this.resourcesRoute = true);
      } else {
        this.resourcesRoute && (this.resourcesRoute = false);
      }
    });
  }

  ngOnInit(): void {
    this.routeLanguageService.routeLanguage.subscribe(res => {
      this.language = res;
      this.cd.markForCheck();
    })
  }

  /**
   * Controls sign up pop-up.
   */
  public manageSignUpPopUp(): void {
    this.signedUpPopUp.emit();
  }

  /**
   * Scrolls to the top of the page.
   */
  public scrollPageToTop(): void {
    this.scrollPage.emit();
  }

  /**
   * Emit language change
   */
  public onLanguageChange(): void {
    this.languageChange.emit();
  }

  /**
   * Toggle Mobile Menu
   */
  public toggleMobileMenu(): void {
    this.mobileMenu.toggleMobileMenu();
  }

  public goToSignup(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.document.defaultView.open(this.signupUrl, '_blank');
  }

  public goToLogin(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.document.defaultView.open(this.loginUrl, '_blank');
  }

  public showHideMobileMenu(): void {
    this.isMobileMenuShown = !this.isMobileMenuShown;
  }

  public showNavigationDropdown(): void {
    this.isDropdownHidden && (this.isDropdownHidden = false);
  }

  public hideNavigationDropdown(): void {
    !this.isDropdownHidden && (this.isDropdownHidden = true);
  }
}
